import { useMutation } from '@apollo/client'
import { Button } from '@material-ui/core'
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet'
import AddBoxIcon from '@material-ui/icons/AddBox'
import AssignmentIcon from '@material-ui/icons/Assignment'
import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import apolloClient from '../../graphql/apolloClient'
import { CREATE_WALLET_MUTATION } from '../../graphql/wallet'
import { DateObject } from '../../helpers/date'
import { isValidCreditLimit } from '../../helpers/numbers'
import { getTranslate, lang } from '../../localization'
import { WalletInput, WalletType } from '../../types/wallet'
import StepContent from './stepContents'
import useStyles from './styles/dynamicStepper.styles'

interface Step {
  label: 'Choose Wallet' | 'Create Creditor' | 'Payment Term'
  icon: React.ReactNode
}

export const isNextDisabled = (
  activeStep: number,
  walletInput: WalletInput,
  selectedWalletType: WalletType | undefined,
  assignmentHasFinished: boolean
): boolean => {
  if (!selectedWalletType) return true

  if (activeStep === 0) {
    return (
      !walletInput.entity.entityType ||
      !walletInput.entity.entityID ||
      !walletInput.currency ||
      !walletInput.walletType ||
      (!selectedWalletType.isBilateral &&
        !isValidCreditLimit(walletInput.maxCredit)) ||
      (!selectedWalletType.isBilateral &&
        !isValidCreditLimit(walletInput.maxReservable))
    )
  }

  if (activeStep === 1 && selectedWalletType.isBilateral) {
    return (
      walletInput.creditors.length === 0 ||
      walletInput.creditors.some(
        (creditor) =>
          !creditor.entity.entityType ||
          !creditor.entity.entityID ||
          !isValidCreditLimit(creditor.maxCredit) ||
          !isValidCreditLimit(creditor.maxReservable)
      )
    )
  }

  if (activeStep === 2) {
    return (
      walletInput.paymentTermAssignment.length === 0 && !assignmentHasFinished
    )
  }

  return false
}

const DynamicStepper: React.FC = () => {
  const classes = useStyles()
  const history = useHistory()
  const [activeStep, setActiveStep] = useState<number>(0)
  const [steps, setSteps] = useState<Step[]>([
    {
      label: 'Choose Wallet',
      icon: <AccountBalanceWalletIcon style={{ fontSize: 36 }} />
    }
  ])
  const [selectedWalletType, setselectedWalletType] = useState<WalletType>()
  const [coveringRange, setcoveringRange] = useState<DateObject>({})
  const [hasFinishedAssigning, sethasFinishedAssigning] =
    useState<boolean>(false)
  // Initialize state for wallet input data
  const [walletInput, setwalletInput] = useState<WalletInput>({
    entity: { entityType: '', entityID: '' },
    walletType: '',
    currency: undefined,
    maxCredit: 0,
    active: true,
    canUseCredit: true,
    autoSettelmentEnabled: true,
    maxReservable: 0,
    creditors: [],
    paymentTermAssignment: []
  })
  const [createWallet, { loading: loadingForWalletCreation }] = useMutation(
    CREATE_WALLET_MUTATION,
    {
      onCompleted: (data) => {
        toast.success(
          data.createWallet.message || 'Wallet created successfully!'
        )
      },
      onError: (error) => {
        toast.error(`Error creating wallet: ${error.message}`)
      }
    }
  )

  const handleWalletTypeSelection = (
    walletType: WalletType | undefined
  ): void => {
    if (walletType === undefined) {
      return
    }
    // Set steps based on whether the selected wallet type is bilateral
    if (walletType.isBilateral) {
      setSteps([
        {
          label: 'Choose Wallet',
          icon: <AccountBalanceWalletIcon style={{ fontSize: 36 }} />
        },
        {
          label: 'Create Creditor',
          icon: <AddBoxIcon style={{ fontSize: 36 }} />
        },
        {
          label: 'Payment Term',
          icon: <AssignmentIcon style={{ fontSize: 36 }} />
        }
      ])
    } else {
      setSteps([
        {
          label: 'Choose Wallet',
          icon: <AccountBalanceWalletIcon style={{ fontSize: 36 }} />
        },
        {
          label: 'Payment Term',
          icon: <AssignmentIcon style={{ fontSize: 36 }} />
        }
      ])
    }
    setActiveStep(0)
  }

  useEffect(() => {
    handleWalletTypeSelection(selectedWalletType)
  }, [selectedWalletType])

  const handleNext = (): void => {
    if (activeStep === steps.length - 1 || activeStep === 2) {
      createWallet({ variables: { input: walletInput } })
      history.push(`/${lang}/adopay/wallets`)
      apolloClient.cache.evict({
        fieldName: 'wallets'
      })
    } else {
      if (selectedWalletType?.isBilateral) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1)
      } else {
        setActiveStep(2)
      }
    }
  }

  const handleBack = (): void => {
    if (selectedWalletType?.isBilateral) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1)
    } else {
      setActiveStep(0)
    }
  }

  return (
    <div className={classes.root}>
      <h2>{getTranslate('Create Wallet')}</h2>
      <div className={classes.stepperContainer}>
        <div className={classes.customStepper}>
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <div className={classes.stepIconContainer}>
                <div
                  className={
                    activeStep === index
                      ? `${classes.stepCircle} ${classes.activeStepCircle}`
                      : activeStep > index
                      ? `${classes.stepCircle} ${classes.pastStepCircle}`
                      : `${classes.stepCircle} ${classes.inactiveStepCircle}`
                  }>
                  {step.icon}
                </div>
                <div
                  className={
                    activeStep === index
                      ? `${classes.stepLabel} ${classes.activeStepLabel}`
                      : activeStep > index
                      ? `${classes.stepLabel} ${classes.pastStepLabel}`
                      : `${classes.stepLabel} ${classes.inactiveStepLabel}`
                  }>
                  {getTranslate(step.label)}
                </div>
              </div>
              {(index < steps.length - 1 || steps.length === 1) && (
                <div
                  className={[
                    activeStep > index
                      ? classes.activeConnectorLine
                      : classes.inactiveConnectorLine,
                    steps.length === 1 && classes.singleConnectorLine
                  ].join(' ')}></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className={classes.stepContent}>
        <StepContent
          step={activeStep}
          selectedWalletType={selectedWalletType}
          setselectedWalletType={setselectedWalletType}
          walletInput={walletInput}
          setwalletInput={setwalletInput}
          coveringRange={coveringRange}
          setcoveringRange={setcoveringRange}
          sethasFinishedAssigning={sethasFinishedAssigning}
        />
      </div>
      <div className={classes.buttonContainer}>
        <Button
          className={classes.button}
          disabled={activeStep === 0}
          onClick={handleBack}
          variant="outlined"
          color="primary">
          {getTranslate('Back')}
        </Button>
        <Button
          className={classes.button}
          onClick={handleNext}
          disabled={
            isNextDisabled(
              activeStep,
              walletInput,
              selectedWalletType,
              hasFinishedAssigning
            ) || loadingForWalletCreation
          }
          variant="contained"
          color="primary">
          {activeStep === 2 && steps.length !== 1
            ? getTranslate('Finish')
            : getTranslate('Next')}
        </Button>
      </div>
    </div>
  )
}

export default DynamicStepper
